/**
 * Gate 5 — shared adapters for common operational reads/writes.
 * Components should prefer these over inline `.eq('organization_id', …)`.
 */

import {
  applyOrganizationScope,
  applyOrganizationScopeForActor,
  orgScopedInsert,
  orgScopedUpdateByPk,
  orgScopedUpdateByPkIn,
  resolveOrganizationIdForQueries,
  stampOrganizationId,
} from "./orgQueryScope";
import { getProjectsRelationName } from "./operationalProjections";
import { applyProjectVisibilityFilter } from "./teamScope";
import {
  getArchiveUpdatePayload,
  getRestoreUpdatePayload,
} from "./projectVisibility";
import { applyActiveClientFilter } from "./clientVisibility";
import {
  buildClientByIdMap,
  filterOperationallyActiveProjects,
} from "./operationalLifecycle";
import { resolveIdempotentProjectInsert } from "./projectCreationIdempotency";

const DEFAULT_PROJECT_SELECT = `
  project_id,
  project_name,
  client_id,
  created_by,
  notes,
  organization_id
`;

const CLIENTS_EMBED_RE = /,?\s*clients\s*\([^)]*\)/gi;

/**
 * Drop a PostgREST `clients (...)` embed. Views such as projects_operational
 * and some staging schemas have no inferable projects→clients FK.
 */
export function stripClientsEmbedFromSelect(select = "") {
  return String(select)
    .replace(CLIENTS_EMBED_RE, "")
    .replace(/,\s*,/g, ",")
    .replace(/,\s*$/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function isMissingClientsRelationshipError(error) {
  if (!error) {
    return false;
  }
  const haystack = `${error.code || ""} ${error.message || ""} ${error.details || ""}`;
  return /PGRST200/i.test(haystack) && /clients/i.test(haystack);
}

function withOperationalClientFallback(row) {
  if (!row || row.clients || !row.client_name) {
    return row;
  }
  return {
    ...row,
    clients: { client_name: row.client_name },
  };
}


/**
 * Resolve parent-client archive map for operational filtering.
 * Prefer embedded clients.archived_at; otherwise batch-load clients by id.
 */
async function loadClientArchiveMapForProjects(
  client,
  organizationId,
  projects = [],
) {
  const map = {};
  const missingIds = new Set();

  for (const project of projects || []) {
    if (project?.clients && typeof project.clients === "object") {
      if (project.client_id) {
        map[project.client_id] = project.clients;
      }
      continue;
    }
    if (project?.client_id) {
      missingIds.add(project.client_id);
    }
  }

  if (missingIds.size === 0) {
    return map;
  }

  let query = client
    .from("clients")
    .select("client_id, archived_at, client_status")
    .in("client_id", [...missingIds]);

  query = applyOrganizationScope(query, organizationId, {
    tableName: "clients",
  });

  const { data, error } = await query;
  if (error) {
    throw error;
  }

  Object.assign(map, buildClientByIdMap(data || []));
  return map;
}

/**
 * Load active projects with organization visibility scope.
 * Employees read projects_operational (no budget/hourly_rate).
 */
export async function loadScopedActiveProjects(
  client,
  actor,
  context = {},
  select = DEFAULT_PROJECT_SELECT,
  { activeOnly = true, operationalOnly = null } = {},
) {
  if (!client || !actor) {
    return [];
  }

  // Default ops lists exclude parent-archived clients (hard lock).
  const applyOperationalLock =
    operationalOnly == null ? activeOnly : Boolean(operationalOnly);

  const organizationId = await resolveOrganizationIdForQueries(
    actor,
    client,
    context.organizationId,
  );

  const relation = getProjectsRelationName(actor, context.orgPolicies);

  const execute = async (selectClause) => {
    let query = client.from(relation).select(selectClause);

    if (activeOnly) {
      query = query.eq("is_active", true);
    }

    query = applyOrganizationScope(query, organizationId, {
      tableName: "projects",
    });
    query = applyProjectVisibilityFilter(
      query,
      actor,
      context.directReportIds ?? [],
    );

    return query.order("project_name");
  };

  let { data, error } = await execute(select);

  if (
    error &&
    isMissingClientsRelationshipError(error) &&
    /clients\s*\(/i.test(select)
  ) {
    ({ data, error } = await execute(stripClientsEmbedFromSelect(select)));
  }

  if (error) {
    throw error;
  }

  let rows = (data || []).map(withOperationalClientFallback);

  if (applyOperationalLock && rows.length) {
    const clientById = await loadClientArchiveMapForProjects(
      client,
      organizationId,
      rows,
    );
    rows = filterOperationallyActiveProjects(rows, clientById);
  }

  return rows;
}

/**
 * Load projects by id with organization visibility scope.
 * Used to validate dashboard aggregates against live project records.
 */
export async function loadScopedProjectsByIds(
  client,
  actor,
  projectIds = [],
  context = {},
  { activeOnly = false } = {},
) {
  if (!client || !actor) {
    return [];
  }

  const uniqueIds = [...new Set((projectIds || []).filter(Boolean))];

  if (uniqueIds.length === 0) {
    return [];
  }

  const organizationId = await resolveOrganizationIdForQueries(
    actor,
    client,
    context.organizationId,
  );

  const relation = getProjectsRelationName(actor, context.orgPolicies);
  let query = client
    .from(relation)
    .select(
      "project_id, project_name, status, is_active, created_by, organization_id, created_at, updated_at",
    )
    .in("project_id", uniqueIds);

  if (activeOnly) {
    query = query.eq("is_active", true);
  }

  query = applyOrganizationScope(query, organizationId, {
    tableName: "projects",
  });
  query = applyProjectVisibilityFilter(
    query,
    actor,
    context.directReportIds ?? [],
  );

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data || [];
}

/**
 * Load clients for the actor's organization.
 * Archived clients are excluded by default (`archived_at IS NULL`).
 * Pass includeArchived: true only when a screen explicitly manages archived records.
 */
export async function loadScopedClients(
  client,
  actor,
  select = "client_id, client_name",
  context = {},
  { activeOnly = false, createdBy = null, includeArchived = false } = {},
) {
  if (!client || !actor) {
    return [];
  }

  const organizationId = await resolveOrganizationIdForQueries(
    actor,
    client,
    context.organizationId,
  );

  let query = client.from("clients").select(select);

  query = applyActiveClientFilter(query, { includeArchived });

  if (activeOnly) {
    query = query.eq("client_status", "active");
  }

  if (createdBy) {
    query = query.eq("created_by", createdBy);
  }

  query = applyOrganizationScope(query, organizationId, {
    tableName: "clients",
  });

  const { data, error } = await query.order("client_name");

  if (error) {
    throw error;
  }

  return data || [];
}

/**
 * Insert a client row with organization_id stamped from actor context.
 */
export async function insertScopedClient(
  client,
  actor,
  clientPayload,
  context = {},
) {
  return orgScopedInsert(client, "clients", [clientPayload], actor, {
    organizationId: context.organizationId,
    select: "client_id, client_name",
    single: true,
  });
}

/**
 * Insert a project row with organization_id stamped from actor context.
 * A repeated creation_idempotency_key or source_estimate_id resolves to the
 * existing org project instead of inserting a second row.
 */
export async function insertScopedProject(
  client,
  actor,
  projectPayload,
  context = {},
) {
  const result = await orgScopedInsert(client, "projects", [projectPayload], actor, {
    organizationId: context.organizationId,
    select: "project_id",
    single: true,
  });

  if (!result?.error) {
    return result;
  }

  const replay = await resolveIdempotentProjectInsert(
    client,
    actor,
    projectPayload,
    result.error,
    context,
  );

  return replay || result;
}

/**
 * RG-03 — update a project by id within the actor's organization only.
 */
export async function updateScopedProject(
  client,
  actor,
  projectId,
  payload,
  context = {},
) {
  return orgScopedUpdateByPk(client, "projects", payload, actor, {
    pkColumn: "project_id",
    pkValue: projectId,
    organizationId: context.organizationId,
  });
}

/**
 * RG-03 — update a client by id within the actor's organization only.
 */
export async function updateScopedClient(
  client,
  actor,
  clientId,
  payload,
  context = {},
) {
  return orgScopedUpdateByPk(client, "clients", payload, actor, {
    pkColumn: "client_id",
    pkValue: clientId,
    organizationId: context.organizationId,
  });
}

/**
 * RG-03 — insert a material with organization_id stamped.
 */
export async function insertScopedMaterial(
  client,
  actor,
  materialPayload,
  context = {},
) {
  return orgScopedInsert(client, "materials", [materialPayload], actor, {
    organizationId: context.organizationId,
    select: "material_id",
    single: true,
  });
}

/**
 * RG-03 — update a material by id within the actor's organization only.
 */
export async function updateScopedMaterial(
  client,
  actor,
  materialId,
  payload,
  context = {},
) {
  return orgScopedUpdateByPk(client, "materials", payload, actor, {
    pkColumn: "material_id",
    pkValue: materialId,
    organizationId: context.organizationId,
  });
}

/**
 * RG-03 — soft-archive materials by id within the actor's organization only.
 */
export async function archiveScopedMaterials(
  client,
  actor,
  materialIds,
  context = {},
) {
  return orgScopedUpdateByPkIn(
    client,
    "materials",
    { active: false },
    actor,
    {
      pkColumn: "material_id",
      pkValues: materialIds,
      organizationId: context.organizationId,
    },
  );
}

/**
 * RG-03 — archive / restore project within the actor's organization only.
 */
export async function archiveScopedProject(
  client,
  actor,
  projectId,
  context = {},
) {
  return updateScopedProject(
    client,
    actor,
    projectId,
    getArchiveUpdatePayload(),
    context,
  );
}

export async function restoreScopedProject(
  client,
  actor,
  projectId,
  context = {},
) {
  return updateScopedProject(
    client,
    actor,
    projectId,
    getRestoreUpdatePayload(),
    context,
  );
}

/**
 * Scope an existing Supabase query builder for the current actor's organization.
 */
export async function scopeQueryForActorOrganization(
  query,
  actor,
  client,
  tableName,
  context = {},
) {
  return applyOrganizationScopeForActor(query, actor, client, {
    organizationId: context.organizationId,
    tableName,
  });
}

/**
 * Stamp organization_id onto a payload before a direct insert (escape hatch).
 * Prefer orgScopedInsert when the full builder is available.
 */
export async function stampPayloadForActorOrganization(
  payload,
  actor,
  client,
  context = {},
) {
  const organizationId = await resolveOrganizationIdForQueries(
    actor,
    client,
    context.organizationId,
  );

  return stampOrganizationId(payload, organizationId);
}
